// Entry point: login screen first, then the world.

// The kind of screen this is, marked on the page before anything else is
// loaded or drawn, since the login screen is laid out for a phone or a
// tablet as much as the world is; and the on-screen keyboard measured
// from the start, for the login fields.
import "./input/device";
import "./ui/keyboard";
import type { App } from "./app";
import { BUILD, buildLine } from "./build";
import { device } from "./input/device";
import type { Viewport } from "./render/viewport";
import { guardBack } from "./ui/backstack";
import { el } from "./ui/dom";
import { LoginScreen, type LoginResult } from "./ui/login";
import { applySavedUiScale } from "./ui/scale";
import { keepScreenOn } from "./ui/wakelock";

const ui = document.getElementById("ui") as HTMLElement;
const canvas = document.getElementById("view") as HTMLCanvasElement;
// The interface at the size it was left, from the login screen on.
applySavedUiScale();

// A phone or a tablet: its graphics share one chip with the page, which
// would otherwise blur every frosted panel over the moving world again in
// each frame, so the panels are drawn as plain tint from the start, as
// they are on a processor's own graphics. It may also keep Primverse on
// its home screen, to open full screen without the browser's bars round
// it (public/manifest.webmanifest). A laptop with a touchscreen is left
// as it is: its graphics are a laptop's, and it has the desktop app.
if (device().touch && !device().hybrid) {
  document.body.classList.add("low-power");
  document.head.append(
    el("link", { rel: "manifest", href: `${import.meta.env.BASE_URL}manifest.webmanifest` }),
    el("meta", { name: "theme-color", content: "#04070c" }),
  );
}

let viewport: Viewport | null = null;
let app: App | null = null;

// Which build this is, quietly in the bottom corner on every screen, and
// the grid's own version beside it once there is a grid. On the page
// itself rather than in the interface, which the world clears and
// rebuilds. A click copies it, with the build time, for a bug report.
const buildTag = el("button", { class: "build-tag", type: "button" }, buildLine());
buildTag.title = `Built ${new Date(BUILD.built).toLocaleString()}. Click to copy.`;
buildTag.addEventListener("click", () => {
  void navigator.clipboard?.writeText(`${buildTag.textContent} (built ${BUILD.built})`).then(() => {
    buildTag.classList.add("copied");
    setTimeout(() => buildTag.classList.remove("copied"), 900);
  });
});
document.body.append(buildTag);

function showLogin(error?: string): void {
  const screen = new LoginScreen((result) => {
    screen.dispose();
    screen.root.remove();
    void enter(result);
  });
  if (error) screen.showError(error);
  ui.append(screen.root);
}

async function enter(result: LoginResult): Promise<void> {
  const starting = el("div", { class: "overlay-message viewer-startup" },
    el("div", { class: "box glass" }, el("h2", {}, "Starting the viewer…"), el("p", {}, "Preparing the world viewer.")),
  );
  ui.append(starting);
  try {
    // Keep login lean on phones and slower connections: the renderer and
    // world panels are only downloaded after someone chooses to enter.
    const [{ App }, { Viewport }, { loadGraphics }] = await Promise.all([
      import("./app"),
      import("./render/viewport"),
      import("./render/graphics"),
    ]);
    if (!viewport) {
      viewport = await Viewport.create(canvas);
      viewport.applyGraphics(await loadGraphics());
    }
    starting.remove();
    app = new App(viewport, ui, result);
  } catch (e) {
    starting.remove();
    // What the browser said is for the console; the screen says what to do.
    console.warn("the view could not be made:", e);
    showLogin("Primverse couldn't start drawing in this browser. Reload the page to try again, or try another browser.");
    return;
  }
  buildTag.textContent = buildLine(result.info.engine_version);
  app.onLogout = () => {
    guardBack(false);
    keepScreenOn(false);
    app = null;
    buildTag.textContent = buildLine();
    showLogin();
  };
  // Handy from the console while developing.
  (window as unknown as { starfall: unknown }).starfall = { app, viewport };
  const entering = app;
  await app.start();
  // In the world now, so on a touch screen Back closes what is open rather
  // than leaving the page, and the screen stays on while nobody touches
  // it. Not if the way in ended in a logout already.
  if (app === entering) {
    guardBack(true);
    keepScreenOn(true);
  }
}

if (!("WebTransport" in window) && !("WebSocket" in window)) {
  ui.append(el("div", { class: "overlay-message" }, el("div", { class: "box glass" }, el("h2", {}, "Unsupported browser"), el("p", {}, "Primverse needs WebTransport or WebSockets."))));
} else {
  showLogin();
}
